import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import LearningSession from '../components/learning/LearningSession';
import MockupQuizSession from '../components/quiz/MockupQuizSession';
import ExerciseRunner from '../components/learning/ExerciseRunner';
import { GameRenderer } from '../components/placement';
import { playComplete } from '../utils/sounds';

const API = process.env.REACT_APP_API_URL;
const authHeader = () => ({
  Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
  accept: 'application/json',
});

// ─── HuPath v2 Design Tokens ──────────────────────────────────
const HT = {
  yellow: '#FFD166', yellowSoft: '#FFE9A8', yellowBg: '#FFF8E7', yellowShadow: '#E0B554',
  green: '#88C057', greenDark: '#5E9E3E', greenSoft: '#D4E8C0', greenBg: '#E8F3D9', greenShield: '#C9E4A5',
  cream: '#FFF8E7',
  coral: '#FF8C42', coralDark: '#E76F51', coralPress: '#B8431E',
  pinkBg: '#FCE4E8', pink: '#F4A6B8',
  brown: '#8B6F47', brownDark: '#6B4F2B',
  text: '#2D2D2D', textSoft: '#6B6B6B', textMuted: '#9A9A9A',
  border: '#E8E0CC', white: '#FFFFFF', teal: '#5AC0B5',
  plum: '#5B2E5E', aqua: '#3DC9B5', accent: '#FFD23F',
};
const FONT_DISPLAY = '"Fredoka", "Baloo 2", "Nunito", sans-serif';
const FONT_BODY = '"Nunito", "Baloo 2", sans-serif';
const SHADOW_SM = '0 2px 6px rgba(139,111,71,0.08)';
const SHADOW_MD = '0 4px 16px rgba(139,111,71,0.10)';

// ─── Mission metadata ──
const TYPE_META = {
  topic:   { emoji: '📚', label: 'TOPIC MISSION',   hero: 'Chinh phục chủ đề' },
  grammar: { emoji: '📝', label: 'GRAMMAR MISSION', hero: 'Luyện ngữ pháp' },
  part:    { emoji: '🎯', label: 'PART MISSION',    hero: 'Chữa lỗ hổng' },
};

const SkillMissionPage = () => {
  const { entityType, entityId } = useParams();
  const navigate = useNavigate();

  const [mission, setMission] = useState(null);
  const [phase, setPhase] = useState('loading');
  const [gameResults, setGameResults] = useState([]);
  const [quizResults, setQuizResults] = useState([]);
  const [exerciseResults, setExerciseResults] = useState([]);
  const [error, setError] = useState(null);

  // ── Adaptive v2 state (for topic missions) ──
  const [adaptiveSession, setAdaptiveSession] = useState(null);
  const [adaptiveRoundKey, setAdaptiveRoundKey] = useState(0);
  const [adaptiveLoading, setAdaptiveLoading] = useState(false);
  const adaptiveTimerRef = useRef(Date.now());

  useEffect(() => {
    fetch(`${API}/client/mission/${entityType}/${entityId}`, { headers: authHeader() })
      .then(r => r.ok ? r.json() : Promise.reject(`HTTP ${r.status}`))
      .then(m => { setMission(m); setPhase('intro'); })
      .catch(() => setError('Không tải được mission. Thử lại sau.'));
  }, [entityType, entityId]);

  // ── Start adaptive session for topic ──
  const startAdaptive = useCallback(async () => {
    try {
      const res = await fetch(`${API}/client/mission/${entityType}/${entityId}/adaptive-start`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeader() },
      });
      const data = await res.json();
      if (!res.ok) { setError(data?.detail || `HTTP ${res.status}`); return; }
      setAdaptiveSession(data);
      adaptiveTimerRef.current = Date.now();
      setPhase('game');
    } catch (e) { setError(e.message); }
  }, [entityType, entityId]);

  // ── Handle adaptive answer ──
  const handleAdaptiveAnswer = useCallback(async (isCorrect, selectedLabel) => {
    if (!adaptiveSession || adaptiveLoading) return;
    const responseTimeMs = Date.now() - adaptiveTimerRef.current;
    setAdaptiveLoading(true);
    setGameResults(r => [...r, { tag_id: adaptiveSession.round_data?.tag_id, correct: isCorrect }]);

    try {
      const res = await fetch(`${API}/client/placement-test/answer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify({
          session_id: adaptiveSession.session_id,
          is_correct: isCorrect,
          selected_label: selectedLabel,
          response_time_ms: responseTimeMs,
        }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data?.detail || `HTTP ${res.status}`); setAdaptiveLoading(false); return; }

      if (data.next === 'done') {
        // Submit to finalize mastery (reuse placement submit)
        try {
          await fetch(`${API}/client/placement-test/submit`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...authHeader() },
            body: JSON.stringify({ session_id: adaptiveSession.session_id }),
          });
        } catch (e) { /* silent — mastery update is best-effort */ }

        if (mission?.step2_data) setPhase('quiz-offer');
        else if (mission?.step3_data) setPhase('exercise-offer');
        else setPhase('done');
        setAdaptiveLoading(false);
        return;
      }

      setTimeout(() => {
        setAdaptiveSession(prev => ({
          ...prev,
          round_data: data.round_data,
          round_idx: data.round_idx,
          c_level: data.c_level ?? prev.c_level,
          g_tier: data.g_tier ?? prev.g_tier,
        }));
        setAdaptiveRoundKey(k => k + 1);
        adaptiveTimerRef.current = Date.now();
        setAdaptiveLoading(false);
      }, 400);
    } catch (e) { setError(e.message); setAdaptiveLoading(false); }
  }, [adaptiveSession, adaptiveLoading, mission]);

  const isTopicMission = entityType === 'topic';

  // ─── Step helpers ────────────────────────────────────────────
  const phaseIndex = () => {
    if (phase === 'intro') return 0;
    if (phase === 'game') return 1;
    if (phase === 'quiz-offer' || phase === 'quiz') return 2;
    if (phase === 'exercise-offer' || phase === 'exercise') return 3;
    return 4;
  };
  const totalPhases = 4;

  // ─── Top bar (reused across phases) ─────────────────────────
  const renderTopBar = (badgeText = 'MISSION') => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 28px', background: HT.white, borderBottom: `1px solid ${HT.border}`, flexWrap: 'wrap' }}>
      <div style={{ fontSize: 18, fontWeight: 800, fontFamily: FONT_DISPLAY, color: HT.text }}>HuPath</div>
      <div style={{ width: 1, height: 22, background: HT.border }} />
      <div style={{ background: HT.greenDark, color: HT.white, padding: '6px 14px', borderRadius: 999, fontSize: 12, fontWeight: 800, fontFamily: FONT_DISPLAY, letterSpacing: 1.5 }}>🎯 {badgeText}</div>
      <div style={{ flex: 1, display: 'flex', justifyContent: 'center', gap: 6 }}>
        {Array.from({ length: totalPhases }).map((_, i) => (
          <div key={i} style={{
            width: i < phaseIndex() ? 32 : 20, height: 8, borderRadius: 4,
            background: i < phaseIndex() ? HT.green : (i === phaseIndex() ? HT.greenDark : HT.greenBg),
            transition: 'all 0.3s',
          }} />
        ))}
      </div>
      <button onClick={() => navigate('/dashboard')} style={{
        padding: '6px 14px', border: `1px solid ${HT.border}`, borderRadius: 999,
        background: HT.white, color: HT.text, fontFamily: FONT_BODY, fontSize: 12, fontWeight: 700,
        boxShadow: SHADOW_SM, cursor: 'pointer',
      }}>← Thoát</button>
    </div>
  );

  // ═══ LOADING ═══
  if (phase === 'loading') {
    return (
      <div style={{ minHeight: '100vh', background: HT.yellowBg, fontFamily: FONT_BODY, color: HT.text, display: 'flex', flexDirection: 'column' }}>
        {renderTopBar('LOADING')}
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 40 }}>
          <div style={{
            background: HT.white, borderRadius: 16, padding: '30px 36px',
            border: `1px solid ${HT.border}`, boxShadow: SHADOW_MD,
            textAlign: 'center',
          }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>🔄</div>
            <div style={{ fontSize: 16, fontWeight: 800, fontFamily: FONT_DISPLAY }}>Hu đang chuẩn bị mission...</div>
          </div>
        </div>
      </div>
    );
  }

  // ═══ ERROR ═══
  if (error || !mission) {
    return (
      <div style={{ minHeight: '100vh', background: HT.yellowBg, fontFamily: FONT_BODY, color: HT.text, display: 'flex', flexDirection: 'column' }}>
        {renderTopBar('ERROR')}
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 40 }}>
          <div style={{
            background: HT.white, borderRadius: 16, padding: '32px 36px',
            border: `1px solid ${HT.border}`, boxShadow: SHADOW_MD,
            textAlign: 'center', maxWidth: 420,
          }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>😟</div>
            <div style={{ fontSize: 16, fontWeight: 800, fontFamily: FONT_DISPLAY, marginBottom: 18 }}>{error || 'Mission không tìm thấy'}</div>
            <button onClick={() => navigate('/dashboard')} style={{
              padding: '12px 26px', border: 'none',
              background: HT.coralDark, color: HT.white,
              fontFamily: FONT_DISPLAY, fontSize: 14, fontWeight: 800,
              borderRadius: 999, boxShadow: `0 4px 0 ${HT.coralPress}`, cursor: 'pointer',
            }}>← Về Dashboard</button>
          </div>
        </div>
      </div>
    );
  }

  const entity = mission.entity;
  const meta = TYPE_META[entity.type] || TYPE_META.part;

  // ═══ INTRO ═══
  if (phase === 'intro') {
    const steps = [
      { n: 1, label: 'Chơi 10 câu luyện tập', emoji: '🎮', enabled: !!mission.step1_data, tag: 'L1→L4 adaptive' },
      { n: 2, label: 'Thử 5 mini-quiz',        emoji: '📝', enabled: !!mission.step2_data, tag: 'weight 30%' },
      { n: 3, label: 'Làm 1 bài exercise',     emoji: '🏆', enabled: !!mission.step3_data, tag: 'final boss' },
    ];
    return (
      <div style={{ minHeight: '100vh', background: HT.yellowBg, fontFamily: FONT_BODY, color: HT.text, display: 'flex', flexDirection: 'column' }}>
        {renderTopBar(meta.label)}
        <div style={{ flex: 1, display: 'flex', alignItems: 'stretch' }}>
          {/* LEFT hero */}
          <div className="hidden lg:flex" style={{
            flex: 1.1, padding: '50px 60px',
            background: 'linear-gradient(135deg, #5E9E3E, #4A8830)',
            color: HT.white, flexDirection: 'column', justifyContent: 'center',
            position: 'relative', overflow: 'hidden',
          }}>
            <div style={{ position: 'absolute', top: '15%', right: '10%', fontSize: 80, opacity: 0.15 }}>{meta.emoji}</div>
            <div style={{ position: 'absolute', bottom: '12%', left: '8%', fontSize: 60, opacity: 0.12 }}>⭐</div>

            <div style={{ fontSize: 13, fontWeight: 800, fontFamily: FONT_DISPLAY, color: HT.yellow, letterSpacing: 3 }}>{meta.label}</div>
            <div style={{ fontSize: 40, fontWeight: 800, fontFamily: FONT_DISPLAY, letterSpacing: -1, marginTop: 10, lineHeight: 1.1 }}>
              {meta.hero}<br/>
              <span style={{ color: HT.yellow }}>{entity.name}</span>
            </div>

            {/* Score arrow */}
            <div style={{
              marginTop: 24, display: 'flex', alignItems: 'center', gap: 16,
              background: 'rgba(255,255,255,0.12)',
              border: '1px solid rgba(255,255,255,0.25)', borderRadius: 16,
              padding: '14px 18px', backdropFilter: 'blur(10px)',
            }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 11, fontWeight: 800, fontFamily: FONT_DISPLAY, color: HT.yellow, letterSpacing: 1 }}>HIỆN TẠI</div>
                <div style={{ fontSize: 28, fontWeight: 800, fontFamily: FONT_DISPLAY, color: HT.coral }}>{Math.round(entity.current_score || 0)}</div>
              </div>
              <div style={{ fontSize: 24 }}>→</div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 11, fontWeight: 800, fontFamily: FONT_DISPLAY, color: HT.yellow, letterSpacing: 1 }}>MỤC TIÊU</div>
                <div style={{ fontSize: 28, fontWeight: 800, fontFamily: FONT_DISPLAY, color: HT.aqua }}>{Math.round(entity.target_score || 70)}</div>
              </div>
            </div>

            {mission.warnings?.length > 0 && (
              <div style={{
                marginTop: 16, background: 'rgba(255,210,63,0.15)',
                border: `1px solid ${HT.yellow}`, borderRadius: 12,
                padding: '10px 14px', fontSize: 12, fontWeight: 700,
              }}>
                {mission.warnings.map((w, i) => <div key={i}>⚠ {w}</div>)}
              </div>
            )}
          </div>

          {/* RIGHT rules */}
          <div style={{ flex: 1, padding: '50px 40px', display: 'flex', flexDirection: 'column', justifyContent: 'center', maxWidth: 600, margin: '0 auto' }}>
            <div style={{ fontSize: 13, fontWeight: 800, fontFamily: FONT_DISPLAY, color: HT.greenDark, letterSpacing: 3 }}>3 BƯỚC CHINH PHỤC</div>
            <div style={{ fontSize: 26, fontWeight: 800, fontFamily: FONT_DISPLAY, color: HT.text, marginTop: 6, marginBottom: 20 }}>
              Sẵn sàng chưa? {meta.emoji}
            </div>
            {steps.map(s => (
              <div key={s.n} style={{
                display: 'flex', alignItems: 'flex-start', gap: 14,
                padding: '14px 16px', marginBottom: 10,
                background: HT.white, border: `1px solid ${HT.border}`, borderRadius: 16,
                boxShadow: SHADOW_SM,
                opacity: s.enabled ? 1 : 0.4,
              }}>
                <div style={{
                  width: 44, height: 44, borderRadius: 12, flexShrink: 0,
                  background: HT.greenBg, border: `1px solid ${HT.border}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 22,
                }}>{s.emoji}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 11, fontWeight: 800, fontFamily: FONT_DISPLAY, color: HT.greenDark, letterSpacing: 1 }}>BƯỚC {s.n}</div>
                  <div style={{ fontSize: 15, fontWeight: 800, fontFamily: FONT_DISPLAY, color: HT.text, marginTop: 2 }}>{s.label}</div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: HT.textMuted, marginTop: 2 }}>{s.tag}</div>
                </div>
                {!s.enabled && (
                  <div style={{ fontSize: 10, fontWeight: 800, color: HT.textMuted, alignSelf: 'center' }}>SKIP</div>
                )}
              </div>
            ))}

            <div style={{ display: 'flex', gap: 10, marginTop: 20, flexWrap: 'wrap' }}>
              <button
                onClick={() => {
                  if (isTopicMission) { startAdaptive(); return; }
                  if (mission.step1_data) setPhase('game');
                  else if (mission.step2_data) setPhase('quiz-offer');
                  else if (mission.step3_data) setPhase('exercise-offer');
                  else setPhase('done');
                }}
                style={{
                  flex: 1, minWidth: 200, padding: '16px',
                  border: 'none',
                  background: HT.coralDark, color: HT.white,
                  fontFamily: FONT_DISPLAY, fontSize: 16, fontWeight: 800,
                  borderRadius: 999, boxShadow: `0 4px 0 ${HT.coralPress}`, cursor: 'pointer',
                }}
              >🚀 Bắt đầu mission</button>
              <button
                onClick={() => navigate('/dashboard')}
                style={{
                  padding: '16px 20px', border: `2px solid ${HT.greenDark}`,
                  background: HT.white, color: HT.greenDark,
                  fontFamily: FONT_DISPLAY, fontSize: 13, fontWeight: 800,
                  borderRadius: 999, cursor: 'pointer',
                }}
              >Để sau</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ═══ GAME (v2 adaptive for topic, v1 LearningSession for others) ═══
  if (phase === 'game' && isTopicMission && adaptiveSession) {
    const as = adaptiveSession;
    const roundNumber = (as.round_idx || 0) + 1;
    const total = as.total_rounds || 12;
    const C_META = { 1: { label: 'STARTERS', color: HT.teal, emoji: '🐣' }, 2: { label: 'MOVERS', color: HT.yellow, emoji: '🐥' }, 3: { label: 'FLYERS', color: HT.coral, emoji: '🦅' } };
    const G_META = { 1: '👂 G1', 2: '🖼️ G2', 3: '🔄 G3', 4: '📖 G4', 5: '✏️ G5' };
    const cMeta = C_META[as.c_level] || C_META[1];
    return (
      <div style={{ minHeight: '100vh', background: HT.yellowBg, fontFamily: FONT_BODY, color: HT.text, display: 'flex', flexDirection: 'column' }}>
        {renderTopBar('BƯỚC 1 · ADAPTIVE')}
        {/* Sub-bar with C/G info */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 28px', background: HT.white, borderBottom: `1px solid ${HT.border}`, flexWrap: 'wrap' }}>
          <div style={{ fontSize: 13, fontWeight: 800, fontFamily: FONT_DISPLAY }}>Câu {roundNumber}/{total}</div>
          <div style={{ background: cMeta.color, color: HT.text, padding: '3px 10px', borderRadius: 999, fontSize: 11, fontWeight: 800, fontFamily: FONT_DISPLAY }}>
            {cMeta.emoji} {cMeta.label}
          </div>
          <div style={{ background: HT.greenBg, color: HT.text, padding: '3px 10px', borderRadius: 999, fontSize: 11, fontWeight: 800, fontFamily: FONT_DISPLAY }}>
            {G_META[as.g_tier] || 'G?'}
          </div>
          <div style={{ flex: 1 }} />
          <div style={{ height: 8, flex: 2, maxWidth: 200, background: HT.greenBg, borderRadius: 99, overflow: 'hidden' }}>
            <div style={{ width: `${(roundNumber / total) * 100}%`, height: '100%', background: `linear-gradient(90deg, ${HT.teal}, ${HT.green})`, borderRadius: 99, transition: 'width 0.3s' }} />
          </div>
        </div>
        <div style={{ flex: 1, padding: '40px 32px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <GameRenderer
            key={adaptiveRoundKey}
            roundData={as.round_data}
            onAnswer={handleAdaptiveAnswer}
            palette={HT}
            disabled={adaptiveLoading}
          />
        </div>
      </div>
    );
  }
  if (phase === 'game' && mission.step1_data) {
    return (
      <div style={{ minHeight: '100vh', background: HT.yellowBg, fontFamily: FONT_BODY, color: HT.text, display: 'flex', flexDirection: 'column' }}>
        {renderTopBar('BƯỚC 1 · GAME')}
        <div style={{ flex: 1, padding: '20px 16px' }}>
          <div style={{ maxWidth: 680, margin: '0 auto' }}>
            <LearningSession
              sessionData={mission.step1_data}
              onComplete={(results) => {
                setGameResults(results);
                submitGameResults(results, entity);
                const correct = results.filter(r => r.correct).length;
                const total = results.length;
                const ok = total > 0 && (correct / total) >= 0.5;
                if (ok && mission.step2_data) setPhase('quiz-offer');
                else if (ok && mission.step3_data) setPhase('exercise-offer');
                else setPhase('done');
              }}
            />
          </div>
        </div>
      </div>
    );
  }

  // ═══ QUIZ OFFER (inline, v2) ═══
  if (phase === 'quiz-offer') {
    return (
      <div style={{ minHeight: '100vh', background: HT.yellowBg, fontFamily: FONT_BODY, color: HT.text, display: 'flex', flexDirection: 'column' }}>
        {renderTopBar('BƯỚC 2 · OFFER')}
        <OfferCard
          emoji="📝"
          subtitle="BƯỚC 2 · MINI QUIZ"
          title="Sẵn sàng thử quiz?"
          body={`Bé vừa luyện ${mission.extracted_vocab?.length || gameResults.length || 10} câu. Giờ thử ${mission.step2_data?.quizzes?.length || 5} quiz xem nhớ bao nhiêu nhé!`}
          primary="🎮 Thử ngay"
          onPrimary={() => setPhase('quiz')}
          secondary="Để sau"
          onSecondary={() => setPhase(mission.step3_data ? 'exercise-offer' : 'done')}
        />
      </div>
    );
  }

  // ═══ QUIZ ═══
  if (phase === 'quiz' && mission.step2_data) {
    return (
      <MockupQuizSession
        quizzes={mission.step2_data.quizzes}
        open={true}
        onSubmit={(quiz, answer, correct) => {
          submitQuizAnswer(quiz.id, quiz.exercise_type, answer);
          setQuizResults(r => [...r, { quiz_id: quiz.id, correct }]);
        }}
        onClose={() => {
          const correct = quizResults.filter(r => r.correct).length;
          const total = quizResults.length;
          const passed = total > 0 && correct / total >= 0.6;
          if (passed && mission.step3_data) setPhase('exercise-offer');
          else setPhase('done');
        }}
      />
    );
  }

  // ═══ EXERCISE OFFER ═══
  if (phase === 'exercise-offer') {
    return (
      <div style={{ minHeight: '100vh', background: HT.yellowBg, fontFamily: FONT_BODY, color: HT.text, display: 'flex', flexDirection: 'column' }}>
        {renderTopBar('BƯỚC 3 · FINAL BOSS')}
        <OfferCard
          emoji="🏆"
          subtitle="BƯỚC 3 · FINAL BOSS"
          title="Thử bài cuối?"
          body={`Làm 1 bài "${entity.name}" đầy đủ — đây là bài thi thật, cố gắng hết mình nhé!`}
          primary="🎯 Vào luôn"
          onPrimary={() => setPhase('exercise')}
          secondary="Xem kết quả"
          onSecondary={() => setPhase('done')}
        />
      </div>
    );
  }

  // ═══ EXERCISE ═══
  if (phase === 'exercise' && mission.step3_data) {
    return (
      <div style={{ minHeight: '100vh', background: HT.yellowBg, fontFamily: FONT_BODY, color: HT.text, display: 'flex', flexDirection: 'column' }}>
        {renderTopBar('BƯỚC 3 · EXERCISE')}
        <div style={{ flex: 1, padding: '20px 16px' }}>
          <div style={{ maxWidth: 760, margin: '0 auto' }}>
            <ExerciseRunner
              exercise={mission.step3_data}
              onComplete={(results) => {
                setExerciseResults(results);
                submitExerciseResults(results, entity, mission.step3_data.id);
                playComplete();
                setPhase('done');
              }}
            />
          </div>
        </div>
      </div>
    );
  }

  // ═══ DONE ═══
  const totalStars = computeTotalStars(gameResults, quizResults, exerciseResults);
  const gameCorrect = gameResults.filter(r => r.correct).length;
  const quizCorrect = quizResults.filter(r => r.correct).length;
  const exCorrect = exerciseResults.filter(r => r.is_correct).length;

  return (
    <div style={{ minHeight: '100vh', background: HT.yellowBg, fontFamily: FONT_BODY, color: HT.text, display: 'flex', flexDirection: 'column' }}>
      {renderTopBar('HOÀN THÀNH')}
      <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '1.1fr 1fr' }} className="grid-cols-1 lg:grid-cols-[1.1fr_1fr]">
        {/* LEFT celebration */}
        <div style={{
          padding: '50px 48px',
          background: 'linear-gradient(135deg, #5E9E3E, #4A8830)',
          color: HT.white, display: 'flex', flexDirection: 'column', justifyContent: 'center',
          position: 'relative', overflow: 'hidden', minHeight: 480,
        }}>
          {['🎉', '⭐', '✨', '🎊'].map((e, i) => (
            <div key={i} style={{
              position: 'absolute',
              top: `${8 + (i * 21) % 75}%`, left: `${6 + (i * 29) % 88}%`,
              fontSize: 26 + (i % 3) * 8, opacity: 0.3, pointerEvents: 'none',
              transform: `rotate(${i * 50}deg)`,
            }}>{e}</div>
          ))}

          <div style={{ fontSize: 13, fontWeight: 800, fontFamily: FONT_DISPLAY, color: HT.yellow, letterSpacing: 3 }}>
            🎊 MISSION HOÀN THÀNH
          </div>
          <div style={{ fontSize: 34, fontWeight: 800, fontFamily: FONT_DISPLAY, marginTop: 10, letterSpacing: -1, lineHeight: 1.1 }}>
            Tuyệt vời!<br/>
            <span style={{ color: HT.yellow }}>{entity.name}</span>
          </div>

          <div style={{
            marginTop: 24, background: HT.white, color: HT.text,
            borderRadius: 16, padding: '22px 24px',
            border: `1px solid ${HT.border}`, boxShadow: SHADOW_MD,
            display: 'flex', alignItems: 'center', gap: 16,
          }}>
            <div style={{
              width: 80, height: 80, borderRadius: 16, background: HT.yellowSoft,
              border: `1px solid ${HT.border}`, boxShadow: SHADOW_SM,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 44, flexShrink: 0,
            }}>⭐</div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 800, fontFamily: FONT_DISPLAY, color: HT.greenDark, letterSpacing: 2 }}>PHẦN THƯỞNG</div>
              <div style={{ fontSize: 36, fontWeight: 800, fontFamily: FONT_DISPLAY, color: HT.text }}>{totalStars} ⭐</div>
              <div style={{ fontSize: 12, fontWeight: 700, color: HT.textMuted, marginTop: 2 }}>Hu coins đã cộng vào tài khoản</div>
            </div>
          </div>
        </div>

        {/* RIGHT breakdown */}
        <div style={{ padding: '50px 40px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <div style={{ fontSize: 13, fontWeight: 800, fontFamily: FONT_DISPLAY, color: HT.greenDark, letterSpacing: 3 }}>CHI TIẾT</div>
          <div style={{ fontSize: 26, fontWeight: 800, fontFamily: FONT_DISPLAY, color: HT.text, marginTop: 6, marginBottom: 20 }}>Bé đã làm gì?</div>

          {gameResults.length > 0 && (
            <BreakdownRow icon="🎮" label="Game" correct={gameCorrect} total={gameResults.length} />
          )}
          {quizResults.length > 0 && (
            <BreakdownRow icon="📝" label="Mini Quiz" correct={quizCorrect} total={quizResults.length} />
          )}
          {exerciseResults.length > 0 && (
            <BreakdownRow icon="🏆" label="Exercise" correct={exCorrect} total={exerciseResults.length} />
          )}

          <div style={{ display: 'flex', gap: 10, marginTop: 22 }}>
            <button onClick={() => navigate('/dashboard')} style={{
              flex: 1, padding: '16px', border: 'none',
              background: HT.coralDark, color: HT.white,
              fontFamily: FONT_DISPLAY, fontSize: 16, fontWeight: 800,
              borderRadius: 999, boxShadow: `0 4px 0 ${HT.coralPress}`, cursor: 'pointer',
            }}>← Về Dashboard</button>
            <button onClick={() => window.location.reload()} style={{
              padding: '16px 18px', border: `2px solid ${HT.greenDark}`,
              background: HT.white, color: HT.greenDark,
              fontFamily: FONT_DISPLAY, fontSize: 13, fontWeight: 800,
              borderRadius: 999, cursor: 'pointer',
            }}>🔄 Chơi lại</button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── v2 Offer Card ─────────────────────────────────────────────
const OfferCard = ({ emoji, subtitle, title, body, primary, onPrimary, secondary, onSecondary }) => (
  <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 32 }}>
    <div style={{
      background: HT.white, borderRadius: 16, padding: '36px 40px',
      border: `1px solid ${HT.border}`, boxShadow: SHADOW_MD,
      maxWidth: 520, width: '100%', textAlign: 'center',
    }}>
      <div style={{
        width: 86, height: 86, margin: '0 auto', borderRadius: 16,
        background: HT.yellowSoft, border: `1px solid ${HT.border}`, boxShadow: SHADOW_SM,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 46,
      }}>{emoji}</div>
      <div style={{ fontSize: 12, fontWeight: 800, fontFamily: FONT_DISPLAY, color: HT.greenDark, letterSpacing: 3, marginTop: 20 }}>{subtitle}</div>
      <div style={{ fontSize: 28, fontWeight: 800, fontFamily: FONT_DISPLAY, color: HT.text, marginTop: 6, letterSpacing: -0.5 }}>{title}</div>
      <div style={{ fontSize: 14, fontWeight: 600, color: HT.textMuted, marginTop: 10, lineHeight: 1.55 }}>{body}</div>
      <div style={{ display: 'flex', gap: 10, marginTop: 24, justifyContent: 'center', flexWrap: 'wrap' }}>
        <button onClick={onPrimary} style={{
          flex: 1, minWidth: 160, padding: '14px 20px',
          border: 'none',
          background: HT.coralDark, color: HT.white,
          fontFamily: FONT_DISPLAY, fontSize: 15, fontWeight: 800,
          borderRadius: 999, boxShadow: `0 4px 0 ${HT.coralPress}`, cursor: 'pointer',
        }}>{primary}</button>
        <button onClick={onSecondary} style={{
          padding: '14px 20px', border: `2px solid ${HT.greenDark}`,
          background: HT.white, color: HT.greenDark,
          fontFamily: FONT_DISPLAY, fontSize: 13, fontWeight: 800,
          borderRadius: 999, cursor: 'pointer',
        }}>{secondary}</button>
      </div>
    </div>
  </div>
);

const BreakdownRow = ({ icon, label, correct, total }) => {
  const pct = total > 0 ? Math.round((correct / total) * 100) : 0;
  const color = pct >= 80 ? HT.greenSoft : pct >= 50 ? HT.yellowSoft : HT.pinkBg;
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 14,
      padding: '12px 16px', marginBottom: 10,
      background: HT.white, border: `1px solid ${HT.border}`, borderRadius: 16,
      boxShadow: SHADOW_SM,
    }}>
      <div style={{
        width: 44, height: 44, borderRadius: 12, flexShrink: 0,
        background: color, border: `1px solid ${HT.border}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 22,
      }}>{icon}</div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 13, fontWeight: 800, fontFamily: FONT_DISPLAY, color: HT.text }}>{label}</div>
        <div style={{ fontSize: 11, fontWeight: 700, color: HT.textMuted }}>
          {correct}/{total} đúng · {pct}%
        </div>
      </div>
      <div style={{
        fontSize: 16, fontWeight: 800, fontFamily: FONT_DISPLAY, color: HT.text,
        background: color, padding: '4px 10px', borderRadius: 999,
      }}>{pct}%</div>
    </div>
  );
};

// ═══ Submit helpers ═══

const submitGameResults = async (results, entity) => {
  try {
    await fetch(`${API}/client/learning/submit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeader() },
      body: JSON.stringify({
        game_type: 'session',
        topic_tag_id: entity.type === 'topic' ? entity.id : null,
        results: results.map(r => ({ tag_id: r.tag_id, correct: r.correct })),
      }),
    });
  } catch (e) { /* silent */ }
};

const submitQuizAnswer = async (quizId, exerciseType, answer) => {
  try {
    const q = exerciseType ? `?exercise_type=${encodeURIComponent(exerciseType)}` : '';
    await fetch(`${API}/quizzes/${quizId}/submit${q}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeader() },
      body: JSON.stringify({
        user_answer: Array.isArray(answer) ? answer.join(',') : String(answer),
      }),
    });
  } catch (e) { /* silent */ }
};

const submitExerciseResults = async (results, entity, exerciseId) => {
  try {
    await fetch(`${API}/client/mission/exercise-submit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeader() },
      body: JSON.stringify({
        exercise_id: exerciseId,
        entity_type: entity.type,
        entity_id: entity.id,
        answers: results,
      }),
    });
  } catch (e) { /* silent */ }
};

const computeTotalStars = (gameResults, quizResults, exerciseResults) => {
  const gc = gameResults.filter(r => r.correct).length;
  const qc = quizResults.filter(r => r.correct).length;
  const ec = exerciseResults.filter(r => r.is_correct).length;
  return gc * 2 + qc * 3 + ec * 5 + (ec > 0 ? 10 : 0);
};

export default SkillMissionPage;
